import { useEffect, useRef, useState } from 'react';
import { DEFAULT_ATTENDANCE_SUMMARY_ROWS, attendanceSummaryRowsSchema, type AttendanceReportRules, type AttendanceSummaryRow } from 'shared-models/browser';

export type AttendanceSummaryProps = {
  opened: boolean;
  onClose: () => void;
  rules: AttendanceReportRules;
  onSave: (patch: Partial<AttendanceReportRules>) => Promise<boolean>;
};
const draftFor = (rules: Partial<AttendanceReportRules>) => ({
  summaryRows: rules.summaryRows ?? DEFAULT_ATTENDANCE_SUMMARY_ROWS,
  includeCreditTipsInPay: rules.includeCreditTipsInPay ?? false,
  includeCashTipsInPay: rules.includeCashTipsInPay ?? false,
});

export function useAttendanceSummaryController({ opened, onClose, rules, onSave }: AttendanceSummaryProps) {
  const [draft, setDraft] = useState(() => draftFor(rules));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const baseline = useRef(draft);
  const wasOpened = useRef(false);
  const lock = useRef(false);
  useEffect(() => {
    if (!opened) { wasOpened.current = false; return; }
    if (wasOpened.current) return;
    wasOpened.current = true;
    baseline.current = draftFor(rules); setDraft(baseline.current); setError('');
  }, [opened, rules]);

  const row = (id: AttendanceSummaryRow['id'], patch: Partial<Pick<AttendanceSummaryRow, 'label' | 'visible'>>) => {
    setDraft((previous) => ({ ...previous, summaryRows: previous.summaryRows.map((item) => item.id === id ? { ...item, ...patch } : item) }));
  };
  const move = (index: number, direction: number) => setDraft((previous) => {
    const rows = [...previous.summaryRows];
    const target = index + direction;
    if (target < 0 || target >= rows.length) return previous;
    [rows[index], rows[target]] = [rows[target]!, rows[index]!];
    return { ...previous, summaryRows: rows };
  });
  const save = async () => {
    if (lock.current) return;
    const parsed = attendanceSummaryRowsSchema.safeParse(draft.summaryRows);
    if (!parsed.success) { setError('יש להזין שם לכל שורה'); return; }
    const next = { ...draft, summaryRows: parsed.data };
    const patch: Partial<AttendanceReportRules> = {};
    for (const key of Object.keys(next) as Array<keyof typeof next>) {
      if (JSON.stringify(next[key]) !== JSON.stringify(baseline.current[key])) Object.assign(patch, { [key]: next[key] });
    }
    if (Object.keys(patch).length === 0) { onClose(); return; }
    lock.current = true; setSaving(true); setError('');
    try { if (await onSave(patch)) onClose(); else setError('שמירת ההגדרה נכשלה'); }
    catch { setError('שמירת ההגדרה נכשלה'); }
    finally { lock.current = false; setSaving(false); }
  };
  return { draft, saving, error, row, move, save,
    toggle: (key: 'includeCreditTipsInPay' | 'includeCashTipsInPay', value: boolean) => setDraft((previous) => ({ ...previous, [key]: value })),
    reset: () => setDraft(draftFor({})),
  };
}